import React, { Component } from 'react';
import { checkNetworkStatus } from '@/utils/infrastructure/communication';

/**
 * HOC that provides network status to a class component.
 * @param {React.Component} WrappedComponent - The component to wrap.
 * @param {number} [pollDelay=0] - Optional polling interval in milliseconds (0 to disable)
 * @returns {React.Component} Wrapped component with networkStatus prop.
 */
function withNetworkStatus(WrappedComponent, pollDelay = 0) {
  return class NetworkStatusHOC extends Component {
    state = {
      networkStatus: {
        online: navigator.onLine,
        reason: navigator.onLine ? 'Browser online' : 'Initializing...',
      },
    };

    timerRef = null;

    componentDidMount() {
      if (!this.state.networkStatus.online) {
        this.checkStatus();
      }

      const handleNetworkChange = async () => {
        const status = await checkNetworkStatus();
        this.setState({ networkStatus: status });

        if (!status.online && !this.timerRef) {
          this.timerRef = setInterval(() => this.checkStatus(), pollDelay);
        } else if (status.online && this.timerRef) {
          clearInterval(this.timerRef);
          this.timerRef = null;
        }
      };

      window.addEventListener('online', () => handleNetworkChange());
      window.addEventListener('offline', () => handleNetworkChange());
    }

    componentWillUnmount() {
      window.removeEventListener('online', async () => {});
      window.removeEventListener('offline', async () => {});
      if (this.timerRef) clearInterval(this.timerRef);
    }

    checkStatus = async () => {
      try {
        const status = await checkNetworkStatus();
        this.setState({ networkStatus: status });

        if (status.online && this.timerRef) {
          clearInterval(this.timerRef);
          this.timerRef = null;
        }
      } catch (e) {
        this.setState({
          networkStatus: {
            online: false,
            reason: 'Network check failed',
          },
        });
      }
    };

    render() {
      return <WrappedComponent {...this.props} networkStatus={this.state.networkStatus} />;
    }
  };
}

export default withNetworkStatus;
